/* eslint-disable no-constant-condition */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FormikProps } from 'formik';
import Loading from '@components/Shared/Loader/Loading';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { fetchDataByReferenceId, cleanUpItemsList } from '@store/INV/Transactions/InvIssue/invSlice';
import NeatixModal from '@components/Modal/NeatixModal';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import { TreeSelect } from 'antd';

// ✅ Picker
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';

import ToastSwal from '@components/Shared/Alert/ToastSwal';
// ✅ API (lookup)
import {
  lookupItemsWithUomRowsByItemIds,
  mergeIntoDetailsMap,
  lookupBestRowByBarcode, // ✅ add
  makeDetailsKey
} from '@pages/INV/lookups/itemsWithUomLookup.logic';
import { OnHandQty } from './OnHandQty';

interface LinesProps {
  formik: FormikProps<any>;
  isUpdate?: boolean;
  handleSubmit?: (e?: React.FormEvent<any>) => void;
}

// =========================
// ✅ Lot Picker (Lightweight modal instead of react-select)
// =========================
type LotRow = {
  lotId: any; // null for "All"
  lotNumber?: string | null;
  expirationDate?: string | null;
  qty?: any;
  reservationQty?: any;
};
type ItemType = 'InventoryItem';
const DEFAULT_ITEM_TYPE: ItemType = 'InventoryItem';

const n = (v: any) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

const isTrue = (v: any) => v === true || v === 1 || v === '1' || v === 'true';

// ✅ Pagination
const PAGE_SIZE = 20;

const IssueLines: React.FC<LinesProps> = ({ formik, isUpdate = false }) => {
  const { TreeSubInventoryData } = useGetSubinventoryList();
  const { values } = formik;
  const { i18n, t } = useTranslation();

  const dispatch = useAppDispatch();
  const { itemsList, loading: loading1, error: errorfetchItems } = useAppSelector(state => state.Inv);

  // ✅ Manual issue only = MII
  const isManualIssue = values.code === 'MII';
  const isReferenceIssue = !isManualIssue;

  const isPosted = values.postedFlag === true;
  const showUnitPrice = values.methodTransfer === 2; // لإظهار عمود السهر او لا .. في  امر تحويل

  // ✅ flag من الهيدر  )
  // const hasAssemblyItem = isTrue(values?.hasAssemblyItem);
  const hasAssemblyItem = values?.hasAssemblyItem;

  // ✅ UI permissions by type
  const canPickItem = !isPosted && isManualIssue;
  const canChangeUom = !isPosted && isManualIssue;
  const canAddLine = !isPosted && isManualIssue;
  const canDeleteLine = !isPosted && isManualIssue;
  // const canEditQty = !isPosted;
  const canEditQty = !isPosted && !hasAssemblyItem; // ✅ قفل الكميات لو فيه صنف مجمع

  // =========================
  // ✅ Barcode Quick Add (like MoveLines)
  // =========================
  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const barcodeAbortRef = useRef<AbortController | null>(null);

  const [barcodeTerm, setBarcodeTerm] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);

  useEffect(() => {
    setBarcodeError(null);

    // ✅ focus فقط لو مسموح (Manual + not posted)
    if (!canPickItem) return;

    const h = window.setTimeout(() => barcodeInputRef.current?.focus(), 50);
    return () => window.clearTimeout(h);
  }, [values.inventoryId, values.subInventoryId, canPickItem]);

  useEffect(() => {
    return () => barcodeAbortRef.current?.abort();
  }, []);

  // ✅ Picker
  const { pickItemUom, modalProps } = useItemUomPicker();

  // ✅ detailsMap: itemId|uom -> row (contains qtyByLot/quentity/reservedQty/authorizeNegativeStock)
  const [detailsMap, setDetailsMap] = useState<Map<string, any>>(new Map());
  const detailsMapRef = useRef(detailsMap);
  useEffect(() => {
    detailsMapRef.current = detailsMap;
  }, [detailsMap]);

  // ✅ fast access by itemId (so item name doesn't stay as ID)
  const anyRowByItemId = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of detailsMap.values()) {
      if (!r?.itemId) continue;
      const k = String(r.itemId);
      if (!m.has(k)) m.set(k, r);
    }
    return m;
  }, [detailsMap]);
  const anyRowByItemIdRef = useRef(anyRowByItemId);
  useEffect(() => {
    anyRowByItemIdRef.current = anyRowByItemId;
  }, [anyRowByItemId]);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailsAbortRef = useRef<AbortController | null>(null);

  // ✅ smooth batching for rapid "Show more"
  const detailsTimerRef = useRef<any>(null);
  const detailsReqSeqRef = useRef(0);
  const prevVisibleCountRef = useRef(PAGE_SIZE);

  // ✅ "loading on button" only
  const [loadingMore, setLoadingMore] = useState(false);

  // ✅ UOM preload
  const loadedUomsRef = useRef(new Set<string>());
  const uomsAbortRef = useRef<AbortController | null>(null);
  const [uomsLoading, setUomsLoading] = useState(false);

  // =========================
  // ✅ Pagination: show only 20 lines initially (NO reverse)
  // =========================
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setLoadingMore(false);
    prevVisibleCountRef.current = PAGE_SIZE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.inventoryId, values.subInventoryId, values.referenceId, values.code]);

  const allLines: any[] = values.materialTransactionsLineList || [];

  // ✅ NO reverse: show from start
  const visibleLines: any[] = useMemo(() => {
    return allLines.slice(0, visibleCount);
  }, [allLines, visibleCount]);

  const hasMoreLines = allLines.length > visibleCount;
  // console.log(visibleLines);

  // =========================
  // Helpers
  // =========================
  const lineHasDuplicateItemUom = (itemId: any, uom: any, excludeIndex: number) => {
    if (!itemId || !uom) return false;
    return (values.materialTransactionsLineList || []).some((l: any, i: number) => {
      if (i === excludeIndex) return false;
      return String(l?.transactionItemId || '') === String(itemId) && String(l?.transactionUom || '') === String(uom);
    });
  };

  const resetLine = (line: any) => ({
    ...line,
    transactionItemId: null,
    transactionUom: '',
    lotId: null,
    transactionQuantityIn: 0,
    transactionQuantityOut: 0,
    unitPrice: 0,
    total: 0
  });

  // const getInfoExact = (line: any) => {
  //   if (!line?.transactionItemId || !line?.transactionUom) return null;
  //   return detailsMapRef.current.get(makeDetailsKey(line.transactionItemId, line.transactionUom)) || null;
  // };
  const getInfoExact = (line: any) => {
    if (!line?.transactionItemId || !line?.transactionUom) return null;
    return detailsMap.get(makeDetailsKey(line.transactionItemId, line.transactionUom)) || null;
  };

  const getInfoAnyByItemId = (itemId: any) => {
    if (!itemId) return null;
    return anyRowByItemIdRef.current.get(String(itemId)) || null;
  };

  const getInfoLoose = (line: any) => {
    const exact = getInfoExact(line);
    if (exact) return exact;
    return getInfoAnyByItemId(line?.transactionItemId);
  };

  const lotAvailable = (lot: any) => Math.max(0, n(lot?.qty) - n(lot?.reservationQty));

  const getAvailableQtyForLine = (line: any, info: any) => {
    if (!info) return 0;

    const lots = Array.isArray(info.qtyByLot) ? info.qtyByLot : [];
    if (lots.length > 0) {
      // لو مختار لوت -> المتاح لذلك اللوت، لو الكل -> مجموع اللوتات
      if (line?.lotId == null) return lots.reduce((s: number, x: any) => s + lotAvailable(x), 0);
      const lot = lots.find((x: any) => String(x?.lotId) === String(line?.lotId));
      return lot ? lotAvailable(lot) : 0;
    }

    // fallback to inventory quantities
    return Math.max(0, n(info.quentity) - n(info.reservedQty));
  };

  const getRemainingFromLine = (line: any) => {
    if (line?.RemainingQty != null) return Math.max(0, n(line.RemainingQty));
    return undefined;
  };

  // ✅ per-item negative only
  const canNegativeByItem = (info: any) => isTrue(info?.authorizeNegativeStock);

  /**
   * ✅ max allowed qty rules:
   * - Manual (MII):
   *    - authorizeNegativeStock=true => ∞
   *    - else => Available
   * - Non-manual:
   *    - always <= RemainingQty
   *    - if authorizeNegativeStock=false => also <= Available (min(Remaining, Available))
   *    - if authorizeNegativeStock=true => allow exceed Available but still <= Remaining
   */
  const getMaxAllowedQty = (line: any, info: any) => {
    const available = getAvailableQtyForLine(line, info);
    const remaining = getRemainingFromLine(line);

    if (isManualIssue) {
      return canNegativeByItem(info) ? Number.POSITIVE_INFINITY : available;
    }

    const cap = remaining != null ? remaining : 0;
    if (canNegativeByItem(info)) return cap;
    return Math.min(cap, available);
  };

  const clampQty = (line: any, info: any) => {
    const max = getMaxAllowedQty(line, info);
    if (!Number.isFinite(max)) return line;
    const q = n(line.transactionQuantityIn);
    if (q <= max) return line;
    return { ...line, transactionQuantityIn: max };
  };

  const getItemNameForLine = (line: any) => {
    const info = getInfoLoose(line);
    const isEn = i18n.language === 'en';
    const name = (isEn ? info?.name || info?.name2 : info?.name2 || info?.name) || '';
    if (name) return name;

    // fallback from line if exists
    return (
      (isEn
        ? line?.itemName || line?.transactionItemName || line?.itemName2 || line?.transactionItemName2
        : line?.itemName2 || line?.transactionItemName2 || line?.itemName || line?.transactionItemName) || ''
    );
  };

  const getUomNameForLine = (line: any) => {
    const info = getInfoLoose(line);
    const isEn = i18n.language === 'en';
    return (
      (isEn
        ? info?.unitOfMeasureName || info?.unitOfMeasureName2
        : info?.unitOfMeasureName2 || info?.unitOfMeasureName) || ''
    );
  };

  const getCodeOrBarcodeForLine = (line: any) => {
    const info = getInfoLoose(line);
    return info?.barcode || info?.itemCode || line?.itemCode || line?.barcode || '-';
  };

  // =========================
  // ✅ UOM options per itemId (from detailsMap only)
  // =========================
  const uomsByItemId = useMemo(() => {
    const map = new Map<string, any[]>();

    for (const r of detailsMap.values()) {
      if (!r?.itemId) continue;
      const list = map.get(String(r.itemId)) ?? [];
      list.push(r);
      map.set(String(r.itemId), list);
    }

    for (const [itemId, list] of map.entries()) {
      const uniq = new Map<string, any>();
      for (const r of list) uniq.set(String(r.unitOfMeasure), r);

      const arr = Array.from(uniq.values());
      arr.sort((a, b) => Number(!!b.isPrimaryUOM) - Number(!!a.isPrimaryUOM));
      map.set(itemId, arr);
    }

    return map;
  }, [detailsMap]);

  // =========================
  // ✅ fetch UOMs for item (include dynamicFields)
  // =========================
  const ensureItemUomsLoaded = async (itemId: string) => {
    if (!values.inventoryId || !itemId) return;

    const key = `${values.inventoryId}|${values.subInventoryId || ''}|${DEFAULT_ITEM_TYPE}|${itemId}`;
    if (loadedUomsRef.current.has(key)) return;
    loadedUomsRef.current.add(key);

    try {
      uomsAbortRef.current?.abort();
      uomsAbortRef.current = new AbortController();
      setUomsLoading(true);

      const params: any = { InventoryId: values.inventoryId, Scope: 1 };
      if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

      const rows = await lookupItemsWithUomRowsByItemIds({
        params,
        itemType: DEFAULT_ITEM_TYPE,
        itemIds: [itemId],
        pageSize: 3000,
        pageIndex: 1,
        chunkSize: 200,
        bodyExtra: {
          dynamicFields: ['qtyByLot', 'quentity', 'reservedQty']
        },
        opts: {
          signal: uomsAbortRef.current?.signal,
          cache: true,
          dedupeInFlight: true
        }
      });

      if (!rows.length) return;

      setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
    } catch (e: any) {
      loadedUomsRef.current.delete(key);
      if (e?.name !== 'CanceledError') console.error(e);
    } finally {
      setUomsLoading(false);
    }
  };

  const hasQtyData = (info: any) => {
    const hasLots = Array.isArray(info?.qtyByLot) && info.qtyByLot.length > 0;
    const hasQ = info?.quentity != null && info.quentity !== '';
    const hasRes = info?.reservedQty != null && info.reservedQty !== '';
    return hasLots || hasQ || hasRes;
  };

  const addLineByBarcode = async (rawBarcode: string) => {
    const barcode = String(rawBarcode || '').trim();
    if (!barcode) return;

    setBarcodeError(null);

    // ✅ only manual + not posted
    if (isPosted || !canPickItem) return;

    if (!values.inventoryId) {
      const msg = i18n.language === 'en' ? 'Select inventory first.' : 'اختر المخزن أولاً.';
      setBarcodeError(msg);
      ToastSwal({ position: 'bottom-start', text: msg });
      barcodeInputRef.current?.focus();
      return;
    }

    barcodeAbortRef.current?.abort();
    barcodeAbortRef.current = new AbortController();

    setBarcodeLoading(true);

    try {
      const params: any = { InventoryId: values.inventoryId, Scope: 1 };
      if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

      const picked: any = await lookupBestRowByBarcode({
        params,
        itemType: DEFAULT_ITEM_TYPE,
        barcode,
        pageSize: 30,
        pageIndex: 1,
        barcodeField: 'barcodeEqual',
        opts: { signal: barcodeAbortRef.current.signal }
      });

      if (!picked?.itemId || !picked?.unitOfMeasure) {
        const msg = i18n.language === 'en' ? 'Barcode not found.' : 'لا يوجد صنف بهذا الباركود.';
        setBarcodeError(msg);
        ToastSwal({ position: 'bottom-start', text: msg });
        barcodeInputRef.current?.focus();
        return;
      }

      // ✅ store row for display
      setDetailsMap(prev => mergeIntoDetailsMap(prev, [picked]));

      // ✅ preload UOMs/details for this item
      ensureItemUomsLoaded(String(picked.itemId));

      const lines = [...(values.materialTransactionsLineList || [])];

      const existingIndex = lines.findIndex(
        (l: any) =>
          String(l?.transactionItemId || '') === String(picked.itemId) &&
          String(l?.transactionUom || '') === String(picked.unitOfMeasure)
      );

      // ✅ if exists => increment qty (respect qty lock)
      if (existingIndex >= 0) {
        if (!canEditQty) {
          const msg = i18n.language === 'en' ? 'Quantity is locked.' : 'الكميات مقفلة.';
          ToastSwal({ position: 'bottom-start', text: msg });
        } else {
          const current = { ...lines[existingIndex] };
          const next = { ...current, transactionQuantityIn: n(current.transactionQuantityIn) + 1 };

          const finalLine = hasQtyData(picked) ? clampQty(next, picked) : next;

          // ✅ move updated line to top so user sees it مباشرة
          lines.splice(existingIndex, 1);
          lines.unshift(finalLine);

          formik.setFieldValue('materialTransactionsLineList', lines);
        }

        setBarcodeTerm('');
        barcodeInputRef.current?.focus();
        return;
      }

      // ✅ new line (prepend to appear in visible slice)
      if (!canAddLine) return;

      const newLine: any = {
        id: null,
        transactionItemId: picked.itemId,
        transactionUom: picked.unitOfMeasure,
        unitPrice: 0,
        transactionQuantityIn: 1,
        transactionQuantityOut: 0,
        lotId: null,
        inventoryId: values.inventoryId ?? '',
        discount: 0,
        codeCombinationId: '',
        currencyCode: '',
        currencyConversionRate: 0,
        referenceIdMaster: '',
        referenceIdLine: '',
        transactionTypeId: '',
        equivalent: 0,
        expensesAmount: 0,
        batchId: '',
        total: 0
      };

      const finalNew = hasQtyData(picked) ? clampQty(newLine, picked) : newLine;

      formik.setFieldValue('materialTransactionsLineList', [finalNew, ...lines]);

      setBarcodeTerm('');
      barcodeInputRef.current?.focus();
    } catch (e: any) {
      if (e?.name !== 'CanceledError') {
        console.error(e);
        const msg = i18n.language === 'en' ? 'Failed to lookup barcode.' : 'تعذر البحث عن الباركود.';
        setBarcodeError(msg);
        ToastSwal({ position: 'bottom-start', text: msg });
      }
    } finally {
      setBarcodeLoading(false);
    }
  };

  // =========================
  // ✅ preload details for VISIBLE lines only
  // ✅ + smooth debounce
  // ✅ + Abort on rapid show more
  // ✅ + loading only on button (loadingMore)
  // =========================
  const visibleLinesKey = useMemo(() => {
    return visibleLines.map((l: any) => `${l?.transactionItemId || ''}:${l?.transactionUom || ''}`).join('|');
  }, [visibleLines]);

  useEffect(() => {
    const isLoadMore = visibleCount > prevVisibleCountRef.current;
    prevVisibleCountRef.current = visibleCount;

    if (isLoadMore) setLoadingMore(true);

    // clear any scheduled run
    if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);

    // abort any in-flight request
    detailsAbortRef.current?.abort();
    detailsAbortRef.current = null;

    detailsTimerRef.current = setTimeout(async () => {
      if (!values.inventoryId) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const lines = visibleLines || [];
      const valid = lines.filter((l: any) => l?.transactionItemId && l?.transactionUom);
      if (valid.length === 0) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const mapNow = detailsMapRef.current;
      const missingItemIds = new Set<string>();

      for (const l of valid) {
        const k = makeDetailsKey(l.transactionItemId, l.transactionUom);
        if (!mapNow.has(k)) missingItemIds.add(String(l.transactionItemId));
      }

      // nothing missing => stop button loading
      if (missingItemIds.size === 0) {
        if (isLoadMore) setLoadingMore(false);
        return;
      }

      const seq = ++detailsReqSeqRef.current;
      const thisIsLoadMore = isLoadMore;

      detailsAbortRef.current?.abort();
      detailsAbortRef.current = new AbortController();

      setDetailsLoading(true);

      try {
        const params: any = { InventoryId: values.inventoryId, Scope: 1 };
        if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

        const ids = Array.from(missingItemIds);

        const rows = await lookupItemsWithUomRowsByItemIds({
          params,
          itemType: DEFAULT_ITEM_TYPE,
          itemIds: ids,
          pageSize: 3000,
          pageIndex: 1,
          chunkSize: 200,
          bodyExtra: {
            dynamicFields: ['qtyByLot', 'quentity', 'reservedQty']
          },
          opts: {
            signal: detailsAbortRef.current?.signal,
            cache: true,
            dedupeInFlight: true
          }
        });

        setDetailsMap(prev => mergeIntoDetailsMap(prev, rows));
      } catch (e: any) {
        if (e?.name !== 'CanceledError') console.error(e);
      } finally {
        // avoid flicker if newer request started
        if (seq === detailsReqSeqRef.current) {
          setDetailsLoading(false);
          if (thisIsLoadMore) setLoadingMore(false);
        }
      }
    }, 150);

    return () => {
      if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);
      detailsAbortRef.current?.abort();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.inventoryId, values.subInventoryId, visibleLinesKey, visibleCount]);

  // ✅ clear caches when inventory/subInventory changes
  useEffect(() => {
    setDetailsMap(new Map());
    loadedUomsRef.current.clear();
  }, [values.inventoryId, values.subInventoryId]);

  // =========================
  // Reference fetch (same as your old logic)
  // =========================
  // console.log(values.code);
  
  const updateFieldsBasedOnCode = (data: any) => {
    formik.setFieldValue('referenceCode', data?.data?.code ?? data?.data?.orderCode);

    switch (values.code) {
      case 'MOI': //  صرف أمر تحويـل
        formik.setFieldValue('customerId', null);
        formik.setFieldValue('inventoryId', data?.data?.inventoryId);
        formik.setFieldValue('inventoryTo', data?.data?.inventoryTo);
        formik.setFieldValue('methodTransfer', data?.data?.methodTransfer);
        // formik.setFieldValue('inventoryId', null);
        break;
      case 'INV': //
        formik.setFieldValue('customerId', data?.data?.peopleId ?? null);
        formik.setFieldValue('inventoryId', data?.data?.inventoryId);
        break;
      case 'SOI': // أمر بيـــع
        formik.setFieldValue('customerId', data?.data?.peopleId ?? null);
        formik.setFieldValue('supplierId', null);
        formik.setFieldValue('hasAssemblyItem', data?.data?.hasAssemblyItem ?? false);
        break;
      case 'IOI': //
        formik.setFieldValue('subInventoryId', data?.data?.subInventoryId);
        formik.setFieldValue('inventoryId', data?.data?.inventoryId);
        break;
      case 'SPR': //  مردود مشتريات
        formik.setFieldValue('supplierId', data?.data?.peopleId ?? null);
        formik.setFieldValue('customerId', null);
        break;
      case 'SRP': //  مردودات مشتريات أمر شراء
        formik.setFieldValue('supplierId', data?.data?.peopleId);
        formik.setFieldValue('customerId', null);
        break;
      case 'BIL': //
        formik.setFieldValue('supplierId', data?.data?.peopleId);
        formik.setFieldValue('customerId', null);
        break;
      case 'MII': //  صرف متعدد -
        formik.setFieldValue('supplierId', data?.data?.peopleId);
        formik.setFieldValue('customerId', null);
        break;
      case 'IRM': // صرف مواد خـــام
        formik.setFieldValue('supplierId', null);
        formik.setFieldValue('customerId', data?.data?.peopleId);
        break;
      case 'CRT': //
        formik.setFieldValue('supplierId', null);
        formik.setFieldValue('customerId', data?.data?.peopleId);
        break;
      case 'SOP': //  امر بيع نقاط بيع
        formik.setFieldValue('supplierId', null);
        formik.setFieldValue('customerId', data?.data?.peopleId);
        formik.setFieldValue('hasAssemblyItem', data?.data?.hasAssemblyItem ?? false);
        break;
      case 'IOR': //
        formik.setFieldValue('branchId', data?.data?.branchId);
        break;
      case 'WOI': //  أمر صيــانة
        formik.setFieldValue('branchId', data?.data?.workshopId);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (['MII', 'MIR'].includes(values.code)) return;

    if (values.referenceId) {
      dispatch(fetchDataByReferenceId({ referenceId: values.referenceId, code: values.code }))
        .unwrap()
        .then(({ data }) => updateFieldsBasedOnCode(data))
        .catch(err => console.error('Failed to fetch data:', err));
    }

    return () => {
      dispatch(cleanUpItemsList());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.referenceId, values.code]);

  useEffect(() => {
    if (!isUpdate) return;

    const lines = values.materialTransactionsLineList || [];
    if (!lines.length) return;

    const needStamp = lines.some((l: any) => l.__origQty == null);
    if (!needStamp) return;

    const patched = lines.map((l: any) => ({
      ...l,
      __origQty: l.__origQty ?? n(l.transactionQuantityIn) // ✅ تثبيت كمية المستند الحالية وقت فتح شاشة التعديل
    }));

    formik.setFieldValue('materialTransactionsLineList', patched, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate]);

  useEffect(() => {
    if (isUpdate === false) {
      formik?.setFieldValue('materialTransactionsLineList', itemsList);
    } else {
      const EPS = 1e-9;

      const ItemsListForUpdate = (values.materialTransactionsLineList || []).map((m: any) => {
        // ✅ حاول تطابق بالصنف+الوحدة أولاً
        const targetItem: any =
          (itemsList || [])?.find(
            (f: any) =>
              String(f.transactionItemId) === String(m.transactionItemId) &&
              String(f.transactionUom || '') === String(m.transactionUom || '')
          ) ?? (itemsList || [])?.find((f: any) => String(f.transactionItemId) === String(m.transactionItemId));

        const realQty = n(targetItem?.RealQty ?? m?.RealQty ?? 0);

        // ✅ dispensed total من السيرفر (غالباً يشمل المستند الحالي القديم)
        const dispensedTotal = n(targetItem?.dispensedQuantity ?? 0);

        // ✅ استخدم الكمية الأصلية (وقت فتح شاشة التعديل) وليس الحالية
        const origDocQty = n(m.__origQty ?? m.transactionQuantityIn);

        // ✅ المصروف السابق بدون هذا المستند
        const dispensedOther = Math.max(0, dispensedTotal - origDocQty);

        // ✅ المتبقي المسموح للمستند الحالي = real - dispensedOther
        const remainingCap = Math.max(0, realQty - dispensedOther);

        // ✅ مكتمل بسبب مستندات أخرى فقط (حتى لا تمنع تعديل المستند الحالي إذا هو سبب الاكتمال)
        const fullyByOther = dispensedOther >= realQty - EPS;

        return {
          ...m,
          RealQty: realQty,
          dispensedQuantity: dispensedOther, // ✅ ثابت أثناء التعديل
          RemainingQty: remainingCap, // ✅ ثابت كحد أعلى
          IsFullyProcessed: fullyByOther
        };
      });

      formik?.setFieldValue('materialTransactionsLineList', ItemsListForUpdate, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsList]);

  // =========================
  // Picker (manual only)
  // =========================

  const openPickerForLine = async (lineIndex: number) => {
    const lines = values.materialTransactionsLineList || [];
    const line = lines[lineIndex];
    if (!line) return;

    if (!values.inventoryId) {
      window?.alert?.(t('يجب اختيار المخزن أولاً.'));
      return;
    }

    if (!canPickItem) return;

    const params: any = { InventoryId: values.inventoryId, Scope: 1 };
    if (values.subInventoryId) params.SubInventoryId = values.subInventoryId;

    const picked = await pickItemUom({
      title: i18n.language === 'en' ? 'Pick Item & UOM' : 'اختيار الصنف',
      params,
      bodyDefaults: { itemType: DEFAULT_ITEM_TYPE },
      pageSize: 10,
      searchMode: 'manual',
      initialSearchField: 'barcodeEqual',
      isDisabledRow: (r: any) => lineHasDuplicateItemUom(r.itemId, r.unitOfMeasure, lineIndex),
      dynamicFields: ['qtyByLot', 'quentity', 'reservedQty']
    });

    if (!picked) return;

    const row: any = picked.row;
    if (!row?.itemId || !row?.unitOfMeasure) return;

    if (lineHasDuplicateItemUom(row.itemId, row.unitOfMeasure, lineIndex)) {
      window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
      return;
    }

    setDetailsMap(prev => mergeIntoDetailsMap(prev, [row]));
    ensureItemUomsLoaded(String(row.itemId));

    const updated = [...lines];
    updated[lineIndex] = {
      ...resetLine(updated[lineIndex]),
      transactionItemId: row.itemId,
      transactionUom: row.unitOfMeasure,
      lotId: null
    };

    updated[lineIndex] = clampQty(updated[lineIndex], row);
    formik.setFieldValue('materialTransactionsLineList', updated);
  };

  // =========================
  // Line change
  // =========================
  const handleLineChange = (index: number, field: string, value: any) => {
    const updatedLines = [...(values.materialTransactionsLineList || [])];
    if (!updatedLines[index]) return;

    const currentLine = { ...updatedLines[index] };
    const info = getInfoExact(currentLine);

    if (field === 'clearItem') {
      if (!canPickItem) return;
      updatedLines[index] = resetLine(currentLine);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'lotId') {
      if (isPosted) return;

      const next = { ...currentLine, lotId: value };
      const nextInfo = getInfoExact(next) || getInfoAnyByItemId(next?.transactionItemId);
      updatedLines[index] = clampQty(next, nextInfo);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'transactionUom') {
      if (!canChangeUom) return;

      const itemId = currentLine.transactionItemId;
      if (itemId && value && lineHasDuplicateItemUom(itemId, value, index)) {
        window?.alert?.(t('لا يمكن تكرار نفس الصنف بنفس الوحدة.'));
        return;
      }

      const next = { ...currentLine, transactionUom: value, lotId: null };
      if (itemId) ensureItemUomsLoaded(String(itemId));

      const nextInfo = detailsMapRef.current.get(makeDetailsKey(itemId, value)) || getInfoAnyByItemId(itemId);

      updatedLines[index] = clampQty(next, nextInfo);
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    if (field === 'transactionQuantityIn') {
      if (!canEditQty) return;
      if (isReferenceIssue && currentLine?.IsFullyProcessed) return;

      const next = { ...currentLine, transactionQuantityIn: n(value) };
      updatedLines[index] = clampQty(next, info || getInfoAnyByItemId(next?.transactionItemId));
      formik.setFieldValue('materialTransactionsLineList', updatedLines);
      return;
    }

    updatedLines[index] = { ...currentLine, [field]: value };
    formik.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  // =========================
  // Add / Delete
  // =========================
  const handleAddLine = () => {
    if (!canAddLine) return;

    const newLine: any = {
      id: null,
      transactionItemId: null,
      transactionUom: '',
      unitPrice: 0,
      transactionQuantityIn: 0,
      transactionQuantityOut: 0,
      lotId: null,
      inventoryId: '',
      discount: 0,
      codeCombinationId: '',
      currencyCode: '',
      currencyConversionRate: 0,
      referenceIdMaster: '',
      referenceIdLine: '',
      transactionTypeId: '',
      equivalent: 0,
      expensesAmount: 0,
      batchId: ''
    };

    const currentLines = formik.values.materialTransactionsLineList || [];
    formik.setFieldValue('materialTransactionsLineList', [...currentLines, newLine]);
  };

  const handleDeleteLine = (index: number) => {
    if(!canEditQty) return
    // if (!canDeleteLine) return;
    const updatedLines = (values.materialTransactionsLineList || []).filter((_: any, i: number) => i !== index);
    formik.setFieldValue('materialTransactionsLineList', updatedLines);
  };

  // =========================
  // Info modal
  // =========================
  const [show, setShow] = useState(false);
  const [itemDetails, setItemDetails] = useState<any>(null);
  const infoData = useMemo(() => Array.from(detailsMap.values()), [detailsMap]);

  const [lotPickerOpen, setLotPickerOpen] = useState(false);
  const [lotPickerLineIndex, setLotPickerLineIndex] = useState<number | null>(null);
  const [lotPickerRows, setLotPickerRows] = useState<LotRow[]>([]);
  const [lotPickerSelectedLotId, setLotPickerSelectedLotId] = useState<any>(null);
  const [lotPickerItemTitle, setLotPickerItemTitle] = useState<string>('');
  const [lotSearch, setLotSearch] = useState('');

  const openLotPicker = (lineIndex: number, rows: LotRow[], selectedLotId: any, itemTitle: string) => {
    setLotPickerLineIndex(lineIndex);
    setLotPickerRows(rows || []);
    setLotPickerSelectedLotId(selectedLotId ?? null);
    setLotPickerItemTitle(itemTitle || '');
    setLotSearch('');
    setLotPickerOpen(true);
  };

  const closeLotPicker = () => {
    setLotPickerOpen(false);
    setLotPickerLineIndex(null);
    setLotPickerRows([]);
    setLotPickerSelectedLotId(null);
    setLotPickerItemTitle('');
    setLotSearch('');
  };

  const chooseLot = (lotId: any) => {
    if (lotPickerLineIndex == null) return;
    handleLineChange(lotPickerLineIndex, 'lotId', lotId ?? null);
    closeLotPicker();
  };

  const filteredLotRows = useMemo(() => {
    const term = lotSearch.trim().toLowerCase();
    if (!term) return lotPickerRows;

    return (lotPickerRows || []).filter(r =>
      String(r?.lotNumber ?? '')
        .toLowerCase()
        .includes(term)
    );
  }, [lotPickerRows, lotSearch]);

  const showMore = () => {
    // ✅ allow rapid clicks; debounce+abort handles it
    setVisibleCount(v => Math.min(v + PAGE_SIZE, allLines.length));
  };

  const remainingToShow = Math.max(0, Math.min(PAGE_SIZE, allLines.length - visibleCount));

  return (
    <>
      <div className='row'>
        <div className='col-lg-4 mb-1'>
          <div className='input-group flex-nowrap'>
            <span className='input-group-text fw-bold bg-theme bg-opacity-30'>{t('SubInventory')}</span>
            <TreeSelect
              disabled={isPosted}
              loading={loading1}
              showSearch
              className='w-100'
              treeLine
              placeholder={t('Choose')}
              allowClear
              treeDefaultExpandAll={false}
              value={values.subInventoryId}
              onChange={(option: any) => formik.setFieldValue('subInventoryId', option === null ? null : option)}
              treeData={TreeSubInventoryData?.filter((f: any) => f.parentId === values.inventoryId)}
              treeNodeFilterProp='title'
            />
          </div>
        </div>
      </div>

      {/* ✅ IMPORTANT: لا نخلي detailsLoading يخفي المكون */}
      <Loading loading={loading1} error={errorfetchItems} Type='Dots'>
        <ItemUomPickerModal {...modalProps} />

        {/* ✅ Header */}
        <div className='card-header'>
          <div className='d-flex flex-column flex-md-row justify-content-between gap-3'>
            <div className='d-flex align-items-center'>
              <i className='fas fa-dolly-flatbed fs-5 text-theme me-2'></i>
              <h6 className='mb-0 fw-semibold'>
                {t('بنود الصرف المخزني')}
                <span className='badge bg-theme rounded-pill ms-2'>{allLines?.length || 0}</span>
              </h6>

              <span className='badge bg-inverse bg-opacity-25 text-dark ms-2'>
                {isManualIssue
                  ? i18n.language === 'en'
                    ? 'Manual'
                    : 'يدوي'
                  : i18n.language === 'en'
                    ? 'Reference'
                    : 'مرجعي'}
              </span>

              {hasAssemblyItem && (
                <span className='badge bg-warning text-dark ms-2'>
                  {i18n.language === 'en' ? 'Qty locked (Assembly)' : 'الكميات مقفلة (صنف مجمع)'}
                </span>
              )}

              {isPosted && (
                <span className='badge bg-success ms-2'>{i18n.language === 'en' ? 'Posted' : 'مُرحّل'}</span>
              )}
            </div>

            <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2'>
              {(detailsLoading || uomsLoading) && (
                <div className='d-flex align-items-center'>
                  <span className='spinner-border spinner-border-sm text-theme me-2'></span>
                  <small className='text-muted'>
                    {detailsLoading ? t('جاري تحميل التفاصيل...') : t('جاري تحميل الوحدات...')}
                  </small>
                </div>
              )}
              {/* ✅ Barcode Quick Add (Manual only) */}
              {canPickItem && (
                <div className='position-relative w-100 w-md-auto' style={{ minWidth: '240px', maxWidth: '320px' }}>
                  <div className='input-group input-group-sm w-100'>
                    <span className='input-group-text'>
                      <i className='fas fa-barcode text-muted'></i>
                    </span>

                    <input
                      ref={barcodeInputRef}
                      value={barcodeTerm}
                      onChange={e => {
                        setBarcodeTerm(e.target.value);
                        if (barcodeError) setBarcodeError(null);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addLineByBarcode(barcodeTerm);
                        }
                        if (e.key === 'Escape') {
                          setBarcodeTerm('');
                          setBarcodeError(null);
                        }
                      }}
                      className='form-control'
                      disabled={!values.inventoryId || barcodeLoading}
                      placeholder={t('امسح الباركود أو أدخل الرقم')}
                      inputMode='numeric'
                    />

                    {barcodeLoading ? (
                      <span className='input-group-text'>
                        <span className='spinner-border spinner-border-sm text-theme' />
                      </span>
                    ) : (
                      <button
                        className='input-group-text'
                        onClick={() => barcodeTerm && addLineByBarcode(barcodeTerm)}
                        disabled={!barcodeTerm || barcodeLoading || !values.inventoryId}
                        title={t('إضافة')}
                      >
                        <i className='fas fa-plus text-theme'></i>
                      </button>
                    )}
                  </div>

                  {barcodeError && (
                    <div className='position-absolute top-100 start-0 mt-1 w-100' style={{ zIndex: 9999 }}>
                      <div className='alert alert-danger alert-sm py-1 mb-0 d-flex align-items-center'>
                        <i className='fas fa-exclamation-circle me-2'></i>
                        <small>{barcodeError}</small>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {canAddLine && (
                <button
                  type='button'
                  className='btn btn-theme btn-sm d-flex align-items-center justify-content-center px-3 w-100 w-md-auto'
                  onClick={handleAddLine}
                >
                  <i className='fas fa-plus me-2'></i>
                  <span>{t('AddItem')}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ✅ Table */}
        <div className='card-body p-0'>
          <div
            className='table-responsive'
            style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'auto', position: 'relative' }}
          >
            <table className='table table-hover mb-0' style={{ minWidth: '1000px' }}>
              <thead
                className='shadow fw-bold'
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 101,
                  backdropFilter: 'blur(30px)'
                }}
              >
                <tr>
                  <th
                    className='text-center small shadow'
                    style={{ width: '50px', minWidth: '50px', position: 'sticky', left: 0, zIndex: 102 }}
                  >
                    #
                  </th>

                  <th className='text-center small' style={{ width: '400px', minWidth: '400px' }}>
                    {t('ItemName')}
                  </th>

                  <th className='text-center small' style={{ width: '110px', minWidth: '110px' }}>
                    {t('Unit')}
                  </th>

                  <th className='text-center small' style={{ width: '120px', minWidth: '120px' }}>
                    {t('LotNumber')}
                  </th>

                  <th className='text-center small' style={{ width: '80px', minWidth: '80px' }}>
                    {t('Quantity')}
                  </th>

                  {showUnitPrice && (
                    <th className='text-center small' style={{ width: '90px', minWidth: '90px' }}>
                      {t('UnitPrice')}
                    </th>
                  )}

                  <th className='text-center small' style={{ width: '70px', minWidth: '70px' }}></th>
                </tr>
              </thead>

              <tbody className='fw-bold'>
                {visibleLines?.length > 0 ? (
                  visibleLines.map((line: any, idx: number) => {
                    // ✅ NO reverse => Index is direct
                    const Index = idx;

                    const infoExact = getInfoExact(line);
                    const infoLoose = infoExact || getInfoAnyByItemId(line?.transactionItemId);

                    const itemName = line.transactionItemId ? getItemNameForLine(line) : '';
                    const uomName = line.transactionItemId ? getUomNameForLine(line) : '';
                    const codeOrBarcode = line.transactionItemId ? getCodeOrBarcodeForLine(line) : '-';

                    const isDuplicate =
                      isManualIssue &&
                      line.transactionItemId &&
                      line.transactionUom &&
                      (values.materialTransactionsLineList || []).some(
                        (l: any, i: number) =>
                          i !== Index &&
                          String(l?.transactionItemId || '') === String(line.transactionItemId) &&
                          String(l?.transactionUom || '') === String(line.transactionUom)
                      );

                    const uomOptions = line.transactionItemId
                      ? uomsByItemId.get(String(line.transactionItemId)) || []
                      : [];

                    const lots = Array.isArray(infoExact?.qtyByLot) ? infoExact.qtyByLot : [];
                    const allRow = {
                      lotId: null,
                      lotNumber: i18n.language === 'en' ? 'All' : 'الكل',
                      expirationDate: null,
                      reservationQty: lots.reduce((s: number, x: any) => s + n(x?.reservationQty), 0),
                      qty: lots.reduce((s: number, x: any) => s + lotAvailable(x), 0)
                    };
                    const lotRowsWithAll = lots.length > 0 ? [allRow, ...lots] : [allRow];

                    const selectedLot =
                      lotRowsWithAll.find((x: any) => String(x?.lotId ?? '') === String(line.lotId ?? '')) ?? allRow;

                    const availableQty = getAvailableQtyForLine(line, infoExact || infoLoose);
                    const remainingQty = getRemainingFromLine(line);
                    const maxAllowed = getMaxAllowedQty(line, infoExact || infoLoose);
                    const negativeAllowedByItem = canNegativeByItem(infoExact || infoLoose);

                    const qtyDisabled = !canEditQty || (isReferenceIssue && line?.IsFullyProcessed);

                    const displayNo = Index + 1;

                    return (
                      <tr key={line?.id ?? Index}>
                        {/* ✅ sticky # */}
                        <td
                          className='text-center align-middle bg-body shadow'
                          style={{ position: 'sticky', left: 0, zIndex: 100 }}
                        >
                          <span className='badge bg-theme bg-opacity-80 fw-bold' style={{ fontSize: '0.85rem' }}>
                            {displayNo}
                          </span>
                        </td>

                        {/* ✅ Item cell */}
                        <td className='align-middle'>
                          <div style={{ width: '100%' }}>
                            <div className={`p-2 border ${isDuplicate ? 'border-danger' : 'border-theme'} bg-body`}>
                              <div className='d-flex align-items-start gap-2'>
                                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                  {line.transactionItemId ? (
                                    <>
                                      <span className='fw-bold text-truncate d-block'>
                                        {itemName ? (
                                          itemName
                                        ) : detailsLoading ? (
                                          <span className='text-muted'>
                                            <span className='spinner-border spinner-border-sm me-2'></span>
                                            {i18n.language === 'en' ? 'Loading...' : 'جاري التحميل...'}
                                          </span>
                                        ) : (
                                          `ID: ${line.transactionItemId}`
                                        )}

                                        {line?.IsFullyProcessed && (
                                          <span className='badge bg-success ms-2'>
                                            {i18n.language === 'en' ? 'Processed' : 'مكتمل'}
                                          </span>
                                        )}
                                      </span>

                                      <small className='text-muted d-block'>
                                        {t('Unit')}:{' '}
                                        <span className='fw-bold text-theme'>
                                          {/* {uomName || line.transactionUom || '-'} */}
                                          {uomName || '-'}
                                        </span>
                                        <span className='mx-2'>•</span>
                                        {t('Barcode')}:{' '}
                                        <span className='fw-bold'>{String(codeOrBarcode).slice(0, 40)}</span>
                                      </small>
                                    </>
                                  ) : (
                                    <span className='text-muted'>{t('Choose')}</span>
                                  )}

                                  {isDuplicate && (
                                    <small className='text-danger d-block mt-1'>
                                      <i className='fas fa-exclamation-triangle me-1'></i>
                                      {t('هذا الصنف مكرر مع نفس الوحدة')}
                                    </small>
                                  )}
                                </div>

                                {/* Actions */}
                                <div className='d-flex gap-1'>
                                  <button
                                    disabled={!canPickItem || !values.inventoryId}
                                    type='button'
                                    className='btn btn-sm btn-theme rounded-0'
                                    onClick={() => openPickerForLine(Index)}
                                    title={
                                      !values.inventoryId
                                        ? t('يجب اختيار المخزن أولاً.')
                                        : isManualIssue
                                          ? t('اختيار صنف ووحدة')
                                          : t('غير مسموح بهذا النوع')
                                    }
                                  >
                                    <i className='fas fa-search me-1'></i>
                                    {line.transactionItemId ? t('تغيير') : t('اختيار')}
                                  </button>

                                  {line.transactionItemId && (
                                    <button
                                      disabled={!canPickItem}
                                      type='button'
                                      className='btn btn-sm btn-outline-danger rounded-0'
                                      onClick={() => handleLineChange(Index, 'clearItem', true)}
                                      title={t('مسح الصنف')}
                                    >
                                      <i className='fas fa-times'></i>
                                    </button>
                                  )}

                                  <button
                                    disabled={!line.transactionItemId}
                                    type='button'
                                    className='btn btn-sm btn-outline-secondary rounded-0'
                                    onClick={() => {
                                      setItemDetails(line);
                                      setShow(true);
                                    }}
                                    title={t('Info')}
                                  >
                                    <i className='bi bi-info-circle'></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* ✅ UOM */}
                        <td className='align-middle'>
                          <select
                            disabled={!canChangeUom || !line.transactionItemId}
                            className={`form-select form-select-sm bg-body ${
                              isDuplicate ? 'border-danger' : 'border-theme'
                            }`}
                            value={line.transactionUom || ''}
                            onFocus={() => {
                              if (line.transactionItemId) ensureItemUomsLoaded(String(line.transactionItemId));
                            }}
                            onChange={e => handleLineChange(Index, 'transactionUom', e.target.value)}
                            style={{ width: '100%' }}
                          >
                            <option value=''>{t('Choose')}</option>

                            {line.transactionUom && uomOptions.length === 0 && (
                              <option value={line.transactionUom}>{uomName || line.transactionUom}</option>
                            )}

                            {uomOptions.map((u: any) => {
                              const uomVal = u.unitOfMeasure;
                              const label =
                                (i18n.language === 'en'
                                  ? u.unitOfMeasureName || u.unitOfMeasureName2
                                  : u.unitOfMeasureName2 || u.unitOfMeasureName) || `ID: ${uomVal}`;

                              return (
                                <option key={uomVal} value={uomVal}>
                                  {label}
                                </option>
                              );
                            })}

                            {uomsLoading && line.transactionItemId ? (
                              <option value={line.transactionUom || ''} disabled>
                                {i18n.language === 'en' ? 'Loading...' : '...جاري التحميل'}
                              </option>
                            ) : null}
                          </select>
                        </td>

                        {/* ✅ Lot */}
                        <td className='align-middle'>
                          {(() => {
                            const disabled =
                              isPosted || !line.transactionItemId || (isReferenceIssue && line?.IsFullyProcessed);

                            const lotLabel = selectedLot?.lotNumber || (i18n.language === 'en' ? 'All' : 'الكل');

                            return (
                              <div className='d-flex flex-column gap-1'>
                                <div
                                  className='input-group input-group-sm border border-theme'
                                  onClick={() => {
                                    if (disabled) return;
                                    openLotPicker(
                                      Index,
                                      lotRowsWithAll as any,
                                      line.lotId,
                                      itemName || `ID: ${line.transactionItemId}`
                                    );
                                  }}
                                  style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                                >
                                  <input
                                    readOnly
                                    className='form-control form-control-sm  bg-body'
                                    value={lotLabel}
                                    placeholder={t('AllLots')}
                                    disabled={disabled}
                                  />
                                  <span className='input-group-text border-0 bg-body'>
                                    <i className='fas fa-caret-down'></i>
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </td>

                        {/* ✅ Qty */}
                        <td className='align-middle'>
                          <PopoverCustom
                            childern={
                              <input
                                type='number'
                                autoComplete='off'
                                className='form-control form-control-sm text-center px-0 border-theme bg-body'
                                value={line.transactionQuantityIn}
                                disabled={qtyDisabled}
                                onChange={e => handleLineChange(Index, 'transactionQuantityIn', Number(e.target.value))}
                                onInput={e => {
                                  if (isPosted) return;

                                  const input = e.target as HTMLInputElement;
                                  input.value = input.value?.replace(/[^0-9]/g, '');

                                  const entered = Number(input.value);
                                  if (!Number.isFinite(maxAllowed)) return;

                                  if (entered > maxAllowed) input.value = String(maxAllowed);
                                }}
                              />
                            }
                            Header={
                              isPosted
                                ? i18n.language === 'en'
                                  ? `Posted Qty: ${n(line.transactionQuantityIn)} • Onhand now: ${availableQty}`
                                  : `الكمية المُرحلة: ${n(line.transactionQuantityIn)} • المتاح الآن: ${availableQty}`
                                : i18n.language === 'en'
                                  ? `Available: ${availableQty}`
                                  : `${t('AvailableQuantity')} : ${availableQty}`
                            }
                            Body={
                              !(infoExact || infoLoose) ? (
                                <div className='text-muted small'>
                                  {i18n.language === 'en' ? 'Details not loaded.' : 'لم يتم تحميل تفاصيل الصنف.'}
                                </div>
                              ) : isPosted ? (
                                <div className='d-flex flex-column gap-1'>
                                  <div className='d-flex justify-content-between'>
                                    <span>{i18n.language === 'en' ? 'Posted Qty' : 'الكمية المُرحلة'}</span>
                                    <span className='fw-bold text-theme'>{n(line.transactionQuantityIn)}</span>
                                  </div>

                                  <div className='d-flex justify-content-between'>
                                    <span>{i18n.language === 'en' ? 'Onhand now' : 'المتاح الآن'}</span>
                                    <span className='fw-bold text-success'>{availableQty}</span>
                                  </div>

                                  <div className='d-flex justify-content-between'>
                                    <span className='me-4'>
                                      {i18n.language === 'en' ? 'Selected Lot :' : 'رقم التشغيل : '}
                                    </span>
                                    <span className='fw-bold'>
                                      {selectedLot?.lotNumber || (i18n.language === 'en' ? 'All' : 'الكل')}
                                    </span>
                                  </div>

                                  <div className='d-flex justify-content-between'>
                                    <span className='me-4'>
                                      {i18n.language === 'en' ? 'Item Neg Stock' : 'بيع بالسالب :'}
                                    </span>
                                    <span className='fw-bold'>
                                      {negativeAllowedByItem
                                        ? i18n.language === 'en'
                                          ? 'Yes'
                                          : 'مسموح'
                                        : i18n.language === 'en'
                                          ? 'No'
                                          : 'غير مسموح'}
                                    </span>
                                  </div>

                                  {isReferenceIssue && (
                                    <>
                                      <hr className='my-1' />
                                      <div className='d-flex justify-content-between'>
                                        <span>{t('DispensedQuantity')}</span>
                                        <span className='fw-bold text-danger'>{n(line.dispensedQuantity)}</span>
                                      </div>
                                      <div className='d-flex justify-content-between'>
                                        <span>{t('RemainingQuantity')}</span>
                                        <span className='fw-bold text-success'>{n(line.RemainingQty)}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className='d-flex flex-column gap-1'>
                                  {!isManualIssue && (
                                    <div className='d-flex justify-content-between'>
                                      <span>{i18n.language === 'en' ? 'Remaining' : 'المتبقي'}</span>
                                      <span className='fw-bold text-success'>{remainingQty ?? 0}</span>
                                    </div>
                                  )}

                                  <div className='d-flex justify-content-between'>
                                    <span>{i18n.language === 'en' ? 'Max Allowed' : 'الحد المسموح'}</span>
                                    <span className='fw-bold text-theme'>
                                      {Number.isFinite(maxAllowed) ? maxAllowed : '∞'}
                                    </span>
                                  </div>

                                  <div className='d-flex justify-content-between'>
                                    <span>{i18n.language === 'en' ? 'Item Neg Stock' : 'سالب الصنف'}</span>
                                    <span className='fw-bold'>
                                      {negativeAllowedByItem
                                        ? i18n.language === 'en'
                                          ? 'Yes'
                                          : 'مسموح'
                                        : i18n.language === 'en'
                                          ? 'No'
                                          : 'غير مسموح'}
                                    </span>
                                  </div>

                                  <div className='d-flex justify-content-between'>
                                    <span>{i18n.language === 'en' ? 'Selected Lot' : 'اللوت المختار'}</span>
                                    <span className='fw-bold'>
                                      {selectedLot?.lotNumber || (i18n.language === 'en' ? 'All' : 'الكل')}
                                    </span>
                                  </div>

                                  {isReferenceIssue && (
                                    <>
                                      <div className='d-flex justify-content-between'>
                                        <span className='mt-1'>{t('DispensedQuantity')}</span>
                                        <span className='text-danger fw-bold'>{n(line.dispensedQuantity)}</span>
                                      </div>
                                      <div className='d-flex justify-content-between'>
                                        {line.IsFullyProcessed ? (
                                          <span className='text-success fw-bold'>{t('FullyProcessed')}</span>
                                        ) : (
                                          <>
                                            <span className='mt-1'>{t('RemainingQuantity')}</span>
                                            <span className='text-success fw-bold'>{n(line.RemainingQty)}</span>
                                          </>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              )
                            }
                          />
                        </td>

                        {showUnitPrice && (
                          <td className='align-middle'>
                            <input
                              type='number'
                              step='0.000001'
                              autoComplete='off'
                              className='form-control form-control-sm text-center px-1 border-theme bg-body'
                              value={n(line.unitPrice)}
                              disabled
                            />
                          </td>
                        )}

                        {/* ✅ Delete */}
                        <td className='align-middle text-center'>
                          {/* {canDeleteLine ? ( */}
                          {true ? (
                            <button
                              type='button'
                              className='btn btn-sm btn-danger rounded-0'
                              onClick={() => handleDeleteLine(Index)}
                              title={t('Delete Line')}
                              style={{ width: '28px', height: '28px', padding: '0' }}
                            >
                              <i className='fas fa-trash text-white'></i>
                            </button>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className='text-center py-4 text-muted'>
                      <i className='fa fa-inbox fa-2x mb-2'></i>
                      <br />
                      {t('لا توجد أصناف مضافة')}
                      <br />
                      {canAddLine && (
                        <button type='button' className='btn btn-theme mt-3' onClick={handleAddLine}>
                          <i className='fas fa-plus me-2'></i>
                          {t('إضافة اول صنف')}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ✅ Show more (لودنج على الزر فقط) */}
          {hasMoreLines && remainingToShow > 0 && (
            <div className='p-2 text-center'>
              <button type='button' className='btn btn-outline-secondary btn-sm' onClick={showMore}>
                {loadingMore ? (
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                ) : (
                  <i className='fas fa-chevron-down me-2'></i>
                )}

                {loadingMore
                  ? i18n.language === 'en'
                    ? 'Loading...'
                    : 'جاري التحميل...'
                  : i18n.language === 'en'
                    ? `Show more (${remainingToShow})`
                    : `عرض المزيد (${remainingToShow})`}
              </button>

              <div className='text-muted small mt-1'>
                {i18n.language === 'en'
                  ? `Showing ${Math.min(visibleCount, allLines.length)} of ${allLines.length}`
                  : `المعروض ${Math.min(visibleCount, allLines.length)} من ${allLines.length}`}
              </div>
            </div>
          )}

          {/* {formik.errors.materialTransactionsLineList?.length ? <div className='text-danger p-2'> * مطلوب </div> : null} */}
          {Array.isArray(formik.errors.materialTransactionsLineList) && (
            <div className='bg-danger-subtle p-2 text-danger text-center small fw-bold'>
              <small>
                {i18n.language === 'en'
                  ? 'Some lines have missing or invalid data'
                  : 'بعض البنود تحتوي على بيانات ناقصة أو غير صحيحة'}
              </small>
            </div>
          )}
        </div>

        {/* ✅ Card Footer */}
        <div className='card-footer d-flex justify-content-between align-items-center'>
          <div className='text-muted small'>
            <i className='fas fa-list me-1'></i>
            {t('عدد الأصناف')}: <span className='fw-bold'>{allLines?.length || 0}</span>
          </div>

          <div className='text-theme fw-bold'>
            <i className='fas fa-info-circle me-2'></i>
            {isPosted ? (i18n.language === 'en' ? 'Read only (Posted)' : 'قراءة فقط (مُرحّل)') : '-'}
          </div>
        </div>
      </Loading>

      {/* ✅ Lot Picker Modal */}
      <NeatixModal
        show={lotPickerOpen}
        setShow={(v: boolean) => (v ? setLotPickerOpen(true) : closeLotPicker())}
        bodyClassName='p-0'
        size='lg'
        showHeader={false}
        centered
      >
        <div className='p-3'>
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <div className='fw-bold'>
              <i className='fas fa-layer-group me-2'></i>
              {i18n.language === 'en' ? 'Select Lot' : 'اختيار اللوت'}
              {lotPickerItemTitle ? <span className='text-muted ms-2'>— {lotPickerItemTitle}</span> : null}
            </div>

            <button type='button' className='btn btn-sm btn-outline-secondary' onClick={closeLotPicker}>
              <i className='fas fa-times'></i>
            </button>
          </div>

          <div className='input-group input-group-sm mb-2'>
            <span className='input-group-text'>{i18n.language === 'en' ? 'Search' : 'بحث'}</span>
            <input
              className='form-control'
              value={lotSearch}
              onChange={e => setLotSearch(e.target.value)}
              placeholder={i18n.language === 'en' ? 'Type lot number...' : 'اكتب رقم اللوت...'}
              autoFocus
            />
          </div>

          <div className='table-responsive' style={{ maxHeight: '55vh', overflow: 'auto' }}>
            <table className='table table-hover table-sm mb-0'>
              <thead className='bg-theme bg-opacity-10'>
                <tr>
                  <th style={{ width: '45%' }}>{t('LotNumber')}</th>
                  <th style={{ width: '35%' }}>{t('ExpirationDate')}</th>
                  <th className='text-end' style={{ width: '20%' }}>
                    {t('AvailableQuantity')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {(filteredLotRows || []).map((r: any) => {
                  const isAll = r?.lotId == null;
                  const lotNo = isAll ? (i18n.language === 'en' ? 'All' : 'الكل') : r?.lotNumber || '-';
                  const exp = r?.expirationDate ? String(r.expirationDate).split('T')[0] : null;
                  const avail = isAll ? n(r?.qty) : lotAvailable(r);

                  const active = String(lotPickerSelectedLotId ?? '') === String(r?.lotId ?? '');

                  return (
                    <tr
                      key={String(r?.lotId ?? 'ALL')}
                      role='button'
                      className={active ? 'table-active' : ''}
                      onClick={() => chooseLot(r?.lotId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className='fw-bold'>{isAll ? <span className='text-theme'>{lotNo}</span> : lotNo}</td>
                      <td className='text-muted'>
                        {isAll ? '-' : exp || (i18n.language === 'en' ? 'No Exp' : 'بدون تاريخ')}
                      </td>
                      <td className='text-end fw-bold text-theme'>{avail}</td>
                    </tr>
                  );
                })}

                {(!filteredLotRows || filteredLotRows.length === 0) && (
                  <tr>
                    <td colSpan={3} className='text-center text-muted py-4'>
                      {i18n.language === 'en' ? 'No lots found.' : 'لا توجد لوتات مطابقة.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </NeatixModal>

      {/* ✅ Onhand Info Modal */}
      <NeatixModal show={show} setShow={setShow} bodyClassName='p-0' size='lg' showHeader={false} centered>
        <OnHandQty Item={itemDetails} data={infoData} onClose={() => setShow(false)} />
      </NeatixModal>
    </>
  );
};

export default IssueLines;
